/**
 * product-custom.js
 * Small, self-contained enhancements for the custom product page design.
 * No dependency on Dawn internals beyond the public `variant:change` event
 * dispatched by Dawn's <product-info> element, so it won't break if Dawn
 * updates its own JS.
 */
(function () {
  'use strict';

  /* ---------- Size chart dialog ---------- */
    /* ---------- Size chart dialog ----------
     The open button lives inside <variant-selects>, which Dawn replaces
     wholesale on every variant change — so the button is a NEW element after
     each change and a directly-bound listener would be lost. Delegate from
     document instead. The dialog itself is rendered outside <variant-selects>
     and is never swapped, so it can be bound directly. */
  function initSizeChart() {
    function getDialog() {
      return document.querySelector('[data-pc-size-chart-dialog]');
    }

    function openDialog(dialog) {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function closeDialog(dialog) {
      dialog.close ? dialog.close() : dialog.removeAttribute('open');
    }

    document.addEventListener('click', function (event) {
      var dialog = getDialog();
      if (!dialog) return;

      if (event.target.closest('[data-pc-size-chart-open]')) {
        openDialog(dialog);
        return;
      }

      if (event.target.closest('[data-pc-size-chart-close]')) {
        closeDialog(dialog);
        return;
      }

      // Backdrop click: the event target is the dialog itself, and the click
      // coordinates fall outside its rendered box.
      if (event.target === dialog) {
        var rect = dialog.getBoundingClientRect();
        var inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) closeDialog(dialog);
      }
    });
  }

  /* ---------- Hotspot dots: click to reveal the floating card ---------- */
  function initHotspots() {
    var dots = document.querySelectorAll('[data-pc-hotspot-toggle]');
    if (!dots.length) return;

    function closeAll(except) {
      dots.forEach(function (dot) {
        if (dot === except) return;
        var card = dot.parentElement.querySelector('[data-pc-hotspot-card]');
        dot.setAttribute('aria-expanded', 'false');
        if (card) card.setAttribute('hidden', '');
      });
    }

    dots.forEach(function (dot) {
      var card = dot.parentElement.querySelector('[data-pc-hotspot-card]');
      if (!card) return;

      dot.addEventListener('click', function (event) {
        event.stopPropagation();
        var isOpen = dot.getAttribute('aria-expanded') === 'true';
        closeAll(dot);
        dot.setAttribute('aria-expanded', String(!isOpen));
        if (isOpen) {
          card.setAttribute('hidden', '');
        } else {
          card.removeAttribute('hidden');
        }
      });
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('.pc-hotspot')) closeAll();
    });
  }

  /* ---------- Wishlist heart toggle (visual only) ---------- */
  function initWishlistToggle() {
    var buttons = document.querySelectorAll('[data-pc-wish-toggle]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.stopPropagation();
        var pressed = btn.getAttribute('aria-pressed') === 'true';
        btn.setAttribute('aria-pressed', String(!pressed));
      });
    });
  }

  /* ---------- Keep the "In stock" line in sync with variant selection ---------- */
  function initStockStatusSync() {
    var statusEl = document.querySelector('[data-pc-stock-status]');
    if (!statusEl) return;

    document.addEventListener('variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      var dot = statusEl.querySelector('.pc-stock-status__dot');
      var label = statusEl.querySelector('span:last-child');
      if (!dot || !label) return;

      if (variant.available) {
        dot.className = 'pc-stock-status__dot pc-stock-status__dot--in';
        label.textContent = 'In stock now — delivery in 24 to 72 hours in USA';
      } else {
        dot.className = 'pc-stock-status__dot pc-stock-status__dot--out';
        label.textContent = 'Currently out of stock';
      }
    });
  }

  /* ---------- Buy Now: add current variant/qty to cart, then go to checkout ---------- */
  function initBuyNow() {
    var btn = document.querySelector('[data-pc-buy-now]');
    if (!btn) return;

    // Dawn's variant picker keeps the currently-selected variant id in a
    // hidden input named "id" inside the product form — this is required
    // for the native Add to cart button to work at all, so it's a safe,
    // stable hook regardless of theme customizations.
    function getVariantId() {
      var input =
        document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
        document.querySelector('input[name="id"]');
      return input ? input.value : null;
    }

    function getQuantity() {
      var input =
        document.querySelector('.quantity__input') ||
        document.querySelector('input[name="quantity"]');
      var value = input ? parseInt(input.value, 10) : 1;
      return value > 0 ? value : 1;
    }

    btn.addEventListener('click', function () {
      var variantId = getVariantId();
      if (!variantId) {
        console.error('Buy Now: could not find a selected variant id.');
        return;
      }

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      btn.disabled = true;
      btn.textContent = 'Adding…';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: getQuantity() }]
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(function () {
          window.location.href = root + 'checkout';
        })
        .catch(function (err) {
          console.error(err);
          btn.disabled = false;
          btn.textContent = 'Buy Now';
        });
    });
  }

  /* ---------- Put Quantity + Add to cart on one row ----------
     We know quantity_selector's block renders as .product-form__quantity
     (confirmed from main-product.liquid), but buy_buttons renders via a
     separate snippet whose root element/class isn't guaranteed. Rather
     than guess a CSS selector for it, grab whatever DOM element actually
     sits next to the quantity block and wrap the two together. This only
     relocates existing elements — it doesn't rebuild or detach the Add to
     Cart button/form, so nothing about cart functionality changes. */
  function initQuantityCartRow() {
    var qty = document.querySelector('.product-form__quantity');
    if (!qty || qty.closest('.pc-qty-cart-row')) return;

    var next = qty.nextElementSibling;
    if (!next) return;

    var row = document.createElement('div');
    row.className = 'pc-qty-cart-row';
    qty.parentNode.insertBefore(row, qty);
    row.appendChild(qty);
    row.appendChild(next);
  }

  /* ---------- Size card: wrap the Size fieldset + move Size Chart into its header ----------
     Built directly from the real product-variant-picker.liquid /
     product-variant-options.liquid markup: each option is a
     <fieldset class="product-form__input--pill"><legend>Name</legend>
     <input type="radio">...<label>...</label>...</fieldset>, and — since
     custom_liquid blocks render with no wrapper div in main-product.liquid
     — the Size Chart button sits as <variant-selects>'s direct next
     sibling in the DOM. */
  // function initSizeCard() {
  //   var variantSelects = document.querySelector('variant-selects');
  //   if (!variantSelects || variantSelects.closest('.pc-size-card')) return;

  //   var fieldset = variantSelects.querySelector(
  //     'fieldset.product-form__input--pill, fieldset.product-form__input--swatch'
  //   );
  //   if (!fieldset) return;

  //   var legend = fieldset.querySelector('legend');
  //   if (!legend) return;

  //   // Only apply this treatment to the option actually called "Size"
  //   var optionName = legend.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
  //   if (optionName.indexOf('size') === -1) return;

  //   // Build the visible header: icon + "Select size:"
  //   var header = document.createElement('div');
  //   header.className = 'pc-size-card__header';

  //   var labelWrap = document.createElement('span');
  //   labelWrap.className = 'pc-size-card__label';
  //   labelWrap.innerHTML =
  //     '<svg viewBox="0 0 20 20" aria-hidden="true" class="pc-size-card__icon">' +
  //     '<rect x="2" y="5" width="16" height="10" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/>' +
  //     '<path d="M5 5v3M8 5v2M11 5v3M14 5v2" stroke="currentColor" stroke-width="1.1"/>' +
  //     '</svg><span>Select size:</span>';
  //   header.appendChild(labelWrap);

  //   // Keep the real legend for screen readers, just hide it visually
  //   legend.classList.add('visually-hidden');

  //   // Move the Size Chart button (if it exists) into the header, right side
  //   var sizeChartBtn = variantSelects.nextElementSibling;
  //   if (sizeChartBtn && sizeChartBtn.hasAttribute('data-pc-size-chart-open')) {
  //     header.appendChild(sizeChartBtn);
  //   }

  //   fieldset.parentNode.insertBefore(header, fieldset);

  //   // Wrap the whole variant-selects element in the card container
  //   var card = document.createElement('div');
  //   card.className = 'pc-size-card';
  //   variantSelects.parentNode.insertBefore(card, variantSelects);
  //   card.appendChild(variantSelects);
  // }

  function init() {
    initSizeChart();
    initHotspots();
    initWishlistToggle();
    initStockStatusSync();
    initBuyNow();
    initQuantityCartRow();
    initSizeCard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Injects white dot navigation over the product gallery and a
 * black-on-grey progress bar between the gallery and thumbnails.
 * Reads Dawn's existing markup — no gallery snippet changes needed.
 */
(function () {
  const INIT_FLAG = 'sioGalleryNav';

  class ProductGalleryNav {
    constructor(gallery) {
      this.gallery = gallery;
      this.list = gallery.querySelector('.product__media-list');
      if (!this.list) return;

      this.slides = Array.from(this.list.querySelectorAll('.product__media-item'));
      if (this.slides.length < 2) return;

      this.viewer = this.list.closest('slider-component');
      if (!this.viewer) return;

      this.build();
      this.bind();
      this.update();
    }

    build() {
      this.viewer.classList.add('sio-gallery-viewer');

      this.dots = document.createElement('div');
      this.dots.className = 'sio-gallery-dots';

      this.slides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sio-gallery-dots__dot';
        dot.setAttribute('aria-label', 'Go to image ' + (index + 1));
        dot.addEventListener('click', () => this.goTo(index));
        this.dots.appendChild(dot);
      });

      this.viewer.appendChild(this.dots);

      this.progress = document.createElement('div');
      this.progress.className = 'sio-gallery-progress';
      this.progress.style.setProperty('--sio-slide-count', this.slides.length);

      this.bar = document.createElement('span');
      this.bar.className = 'sio-gallery-progress__bar';
      this.progress.appendChild(this.bar);

      this.viewer.insertAdjacentElement('afterend', this.progress);
    }

    bind() {
      this.onScroll = () => {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.update());
      };
      this.list.addEventListener('scroll', this.onScroll, { passive: true });

      // Desktop thumbnail layout swaps the `.is-active` class instead of scrolling.
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.list, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    isScrollable() {
      return this.list.scrollWidth > this.list.clientWidth + 1;
    }

    currentIndex() {
      if (this.isScrollable()) {
        const base = this.slides[0].offsetLeft;
        const pos = this.list.scrollLeft;
        let closest = 0;
        let shortest = Infinity;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - base - pos);
          if (distance < shortest) {
            shortest = distance;
            closest = index;
          }
        });
        return closest;
      }

      const active = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      return active > -1 ? active : 0;
    }

    goTo(index) {
      if (this.isScrollable()) {
        const base = this.slides[0].offsetLeft;
        this.list.scrollTo({
          left: this.slides[index].offsetLeft - base,
          behavior: 'smooth',
        });
        return;
      }

      const thumbs = this.gallery.querySelectorAll('.thumbnail-list__item button');
      if (thumbs[index]) thumbs[index].click();
    }

    update() {
      const index = this.currentIndex();

      Array.from(this.dots.children).forEach((dot, position) => {
        const active = position === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.bar.style.transform = 'translateX(' + index * 100 + '%)';
    }
  }

  function init(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.product__media-wrapper media-gallery').forEach((gallery) => {
      if (gallery.dataset[INIT_FLAG]) return;
      gallery.dataset[INIT_FLAG] = 'true';
      new ProductGalleryNav(gallery);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // The gallery is replaced wholesale when a variant changes, so watch for it.
  new MutationObserver(() => init(document)).observe(document.body, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();